import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { NotificationsLevel } from "../../../lib/enums";
import { show } from "../../../notifications/notifications";
import {
  ErrorResponse,
  ValidationErrorResponse
} from "../../../requests/@types";
import api from "../../../services/api";
import { pluralize } from "../../../utils";
import { HttpError } from "../../commons/erorrs/errors";
import { IReanalyzePayload } from "../@types";
import { GET_SAMPLE_ANALYSIS_STATUS_QUERY_KEY } from "../components/analysistable/integrations_analysis_table/IntegrationsAnalysisTable";
import { ANALYSIS_STATUS_QUERY_KEY } from "./useGetSampleAnalysisStatus";

const INITIAL_PAYLOAD = {
  samples: [],
  tc: false,
  ti_cloud: false,
  cape: false,
  cuckoo: false,
  fireeye: false,
  joe: false,
  rl_auxiliary_analysis: false,
  vmray_tcbase: false,
  dynamic_analysis: []
};

const createPayload = (payload: IReanalyzePayload) => {
  const integrations = payload.integrations.reduce((acc, curr) => {
    if (curr === "rl_dynamic_analysis") {
      return {
        ...acc,
        dynamic_analysis: [...acc.dynamic_analysis, "rl_dynamic_analysis"],
        rl_cloud_sandbox_platform: payload.platform,
        rl_cloud_sandbox_locale: payload.locale,
        rl_cloud_sandbox_geolocation: payload.geolocation,
        rl_cloud_sandbox_timeout: payload.execution_timeout,
        rl_cloud_sandbox_sample_name: payload.sample_name,
        rl_cloud_sandbox_internet_simulation: payload.internet_simulation
      };
    }
    return {
      ...acc,
      [curr]: true,
      dynamic_analysis: ["tc", "ti_cloud"].includes(curr)
        ? acc.dynamic_analysis
        : [...acc.dynamic_analysis, curr]
    };
  }, INITIAL_PAYLOAD);

  const interactive = {
    interactive_platform: payload.interactive_platform,
    interactive_timeout: payload.interactive_timeout,
    interactive_sample_name: payload.interactive_sample_name,
    interactive_geolocation: payload.interactive_geolocation,
    interactive_locale: payload.interactive_locale
  };

  const { dynamic_analysis: analyses } = integrations ?? {};

  return {
    ...integrations,
    ...(analyses.includes("rl_dynamic_analysis_interactive") && interactive),
    samples: payload.samples
  };
};

const getSuccessMessage = (sampleCount: number) => {
  if (sampleCount > 1 && sampleCount <= 5) {
    return "Samples sent for reanalysis.";
  } else if (sampleCount > 5) {
    return "Samples sent for reanalysis. Processing them can take a while, and the appliance performance may be impacted.";
  }
  return "Sample sent for reanalysis.";
};

const useReanalyze = () => {
  const queryClient = useQueryClient();

  const mutationFn = (payload: IReanalyzePayload) => {
    const newPayload = createPayload(payload);
    return api.reanalyze(newPayload);
  };
  const { mutate, mutateAsync, isPending, reset, error } = useMutation({
    mutationFn: (payload: IReanalyzePayload) => mutationFn(payload),
    onSuccess: (_, variables) => {
      show({
        level: NotificationsLevel.SUCCESS,
        message: getSuccessMessage(variables.samples.length)
      });
      queryClient.refetchQueries({ queryKey: [ANALYSIS_STATUS_QUERY_KEY] });
      queryClient.refetchQueries({
        queryKey: [GET_SAMPLE_ANALYSIS_STATUS_QUERY_KEY]
      });
    },
    onError: (_error: ErrorResponse<ValidationErrorResponse>, variables) => {
      const { code, detail } = _error?.data ?? {};
      if (code === HttpError.HTTP_400 && !isEmpty(detail?.fields)) {
        const message = Object.values(detail?.fields ?? {})
          .flat()
          .join("\n");
        show({
          level: NotificationsLevel.ERROR,
          message
        });
        return;
      }
      show({
        level: NotificationsLevel.ERROR,
        message: `Failed to send ${pluralize(
          "sample",
          variables.samples.length
        )} to analysis.`
      });
    }
  });

  return { mutate, mutateAsync, isPending, reset, error };
};

export default useReanalyze;
